"use client";

import { useCallback, useEffect, useRef } from "react";
import { HYPERLIQUID_DEFAULT_CHAIN_ID } from "@cede/utils";
import type { ExchangeSessionResult } from "../types";
import { useAirdropAgentWalletValidation } from "./useAirdropAgentWalletValidation";
import { useAirdropHyperliquidConnection } from "./useAirdropHyperliquidConnection";
import { useAuthenticatedWalletConnection } from "@/hooks/core/useAuthenticatedWalletConnection";
import { WALLET_ACTION_ROLE } from "@/hooks/core/useWalletActionGuard";
import { useBuilderFeeApproval } from "@/hooks/trade/hyperliquid/useBuilderFeeApproval";
import { useHyperliquidAccount } from "@/hooks/trade/hyperliquid/useHyperliquidAccount";
import { useHyperliquidSessionStatus } from "@/hooks/trade/hyperliquid/useHyperliquidSessionStatus";
import { useHyperliquidUnifiedAccountSetup } from "@/hooks/trade/hyperliquid/useHyperliquidUnifiedAccountSetup";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQueryClient } from "@tanstack/react-query";
import { useChainId } from "wagmi";

export function useHyperliquidSession({
  enabled,
  agentName,
}: {
  enabled: boolean;
  agentName?: string;
}): ExchangeSessionResult {
  const { authenticated, walletStatus } = useHubUser();
  const { authenticatedWalletAddress } = useAuthenticatedWalletConnection();
  const hlConnection = useAirdropHyperliquidConnection({ agentName });
  const hlBaseSessionStatus = useHyperliquidSessionStatus({ connection: hlConnection, authenticated, walletStatus });
  const { isAgentValid, isChecking: isCheckingAgent } = useAirdropAgentWalletValidation();
  const chainId = useChainId();
  const isOnRequiredChain = chainId === HYPERLIQUID_DEFAULT_CHAIN_ID;
  const mainWalletAddress = authenticatedWalletAddress ?? hlConnection.connectedAddress;
  const builderFee = useBuilderFeeApproval(mainWalletAddress);
  const accountMode = useHyperliquidUnifiedAccountSetup(mainWalletAddress, enabled && hlBaseSessionStatus === "ready");
  const preparationStep =
    hlBaseSessionStatus !== "ready"
      ? null
      : !builderFee.isApproved
        ? ("builder-fee" as const)
        : !accountMode.usesUnifiedBalanceSource
          ? ("unified" as const)
          : null;

  const sessionStatus =
    hlBaseSessionStatus !== "ready"
      ? hlBaseSessionStatus
      : isCheckingAgent
        ? ("checking-activation" as const)
        : !isAgentValid
          ? ("needs-approval" as const)
          : builderFee.isChecking || builderFee.isApproving
            ? ("checking-activation" as const)
            : !builderFee.isApproved
              ? ("needs-preparation" as const)
              : accountMode.isLoading || accountMode.isEnabling
                ? ("checking-activation" as const)
                : accountMode.usesUnifiedBalanceSource
                  ? ("ready" as const)
                  : ("needs-preparation" as const);

  // Accounts
  const { hlAccounts, selectedAccount, setSelectedAccount } = useHyperliquidAccount();
  const queryClient = useQueryClient();

  // After reconnect, the agent data query re-creates the exchange account
  // on the backend but hubUser.exchangeAccounts is stale. Refetch it once.
  const accountsRefetchedRef = useRef(false);
  useEffect(() => {
    if (enabled && hlBaseSessionStatus === "ready" && hlAccounts.length === 0 && !accountsRefetchedRef.current) {
      accountsRefetchedRef.current = true;
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
    }
    if (hlBaseSessionStatus !== "ready") {
      accountsRefetchedRef.current = false;
    }
  }, [enabled, hlBaseSessionStatus, hlAccounts.length, queryClient]);

  // Setup action (agent status → builder fee → unified account)
  const handleSetupAction = useCallback(() => {
    if (hlBaseSessionStatus !== "ready") {
      hlConnection.updateHyperliquidAgentStatus().catch(() => {});
      return;
    }

    if (!isOnRequiredChain) {
      return;
    }

    if (preparationStep === "builder-fee") {
      builderFee.approveBuilderFee().catch(() => {});
      return;
    }

    if (preparationStep === "unified") {
      accountMode.enableUnifiedAccount().catch(() => {});
    }
  }, [
    hlBaseSessionStatus,
    hlConnection,
    isOnRequiredChain,
    preparationStep,
    builderFee.approveBuilderFee,
    accountMode,
  ]);

  const setupLabel =
    preparationStep === "builder-fee"
      ? "Approve Builder Fee"
      : preparationStep === "unified"
        ? "Almost There: Enable Unified Account"
        : "Finish Setup";

  const isSettingUp =
    hlBaseSessionStatus !== "ready"
      ? hlConnection.isUpdatingAgentStatus
      : !builderFee.isApproved
        ? builderFee.isApproving
        : accountMode.isEnabling;

  return {
    connection: hlConnection,
    sessionStatus,
    accounts: hlAccounts,
    selectedAccount: selectedAccount ?? null,
    setSelectedAccount,
    handleSetupAction,
    isSettingUp,
    requiredWalletRole: WALLET_ACTION_ROLE.MAIN_EVM,
    requiredWalletAddress: mainWalletAddress,
    exchangeAccountAddress: hlConnection.agentData.agentAddress,
    needsDeposit: hlBaseSessionStatus === "needs-preparation",
    preparationStep,
    setupLabel,
    refetchAgentData: hlConnection.refetchAgentData,
  };
}
